import { Coding, Immunization } from "@medplum/fhirtypes";
import { FhirBundleSdk, Smart } from "@metriport/fhir-sdk";
import { ISO_DATE } from "@metriport/shared/common/date";
import dayjs from "dayjs";
import {
  getFirstCodeSpecified,
  CVX_CODE,
  RX_NORM_CODE,
} from "../../../shared-components/fhir-sections/shared";
import { getImmunizationCoding } from "../../table-transforms/series-utils";

export type ImmunizationRowData = {
  id: string;
  immunization: string;
  code: string;
  coding?: Coding;
  manufacturer: string;
  date: string;
  status: string;
  originalData: Smart<Immunization>;
};

export function getImmunizationRows({ bundle }: { bundle: FhirBundleSdk }): ImmunizationRowData[] {
  const immunizations = bundle.getImmunizations();

  return immunizations
    .map(immunization => ({
      id: immunization.id ?? "-",
      immunization: getImmunizationDisplay(immunization),
      code: getImmunizationCode(immunization),
      coding: getImmunizationCoding(immunization),
      manufacturer: immunization.manufacturer?.display || "-",
      date: formatDate(immunization.occurrenceDateTime),
      status: immunization.status || "-",
      originalData: immunization,
    }))
    .sort((a, b) => {
      // Sort by date descending (newest first) using original occurrenceDateTime
      const dateA = dayjs(a.originalData.occurrenceDateTime);
      const dateB = dayjs(b.originalData.occurrenceDateTime);
      const isDateAValid = dateA.isValid();
      const isDateBValid = dateB.isValid();

      if (!isDateAValid && !isDateBValid) return 0;
      if (!isDateAValid) return 1; // push a after b
      if (!isDateBValid) return -1; // push b after a

      return dateB.diff(dateA); // newest first
    });
}

function getImmunizationDisplay(immunization: Immunization): string {
  const codings = immunization.vaccineCode?.coding;
  if (codings && codings.length > 0) {
    const displays = codings.map(coding => coding.display).filter(Boolean);
    if (displays.length > 0) {
      return displays.join(", ");
    }
  }

  if (immunization.vaccineCode?.text) {
    return immunization.vaccineCode.text;
  }

  return "-";
}

function getImmunizationCode(immunization: Immunization): string {
  const coding = getFirstCodeSpecified(immunization.vaccineCode?.coding, [CVX_CODE, RX_NORM_CODE]);
  return coding ? `${coding.system}: ${coding.code}` : "-";
}

function formatDate(date: string | undefined): string {
  if (!date || date === "-") return "-";
  try {
    const parsed = dayjs(date);
    if (!parsed.isValid()) return "-";
    return parsed.format(ISO_DATE);
  } catch {
    return "-";
  }
}
